import React, { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Icon,
  Skeleton,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { FaRegFileAlt } from "react-icons/fa";
import dayjs from "dayjs";
import services from "../../views/Orders/services";
import { estimateSectionHeaderProps } from "./utils/estimateSectionHeaderStyles";

type PartConfirmationRequest = {
  requestId: string;
  createdAt?: string;
  status: string;
  supplierId: string;
  supplierName: string;
  parts: Array<{
    partId: string;
    partNumber: string;
    partName: string;
    askedQty: number;
    confirmedQty: number | null;
    responseStatus: string;
    isRevoked: boolean;
  }>;
};

const statusColor = (status: string) => {
  if (status === "PENDING") return "orange";
  if (status === "RESPONDED") return "green";
  if (status === "REVOKED") return "red";
  return "gray";
};

const AllPartConfirmationsView: React.FC<{
  isOpen: boolean;
  orderNo: string;
}> = ({ isOpen, orderNo }) => {
  const [requests, setRequests] = useState<PartConfirmationRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const loadRequests = () => {
    if (!isOpen || !orderNo) return;
    setLoading(true);
    setError(null);
    services
      .getPartConfirmationRequests(orderNo)
      .then((res: { requests?: PartConfirmationRequest[] }) => {
        setRequests(res?.requests || []);
        setSelectedIndex(0);
      })
      .catch(() => {
        setError("Failed to fetch part confirmation requests. Please try again.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadRequests();
  }, [isOpen, orderNo]);

  const selected = requests[selectedIndex];

  const handleRevoke = async (requestId: string, event?: React.MouseEvent) => {
    event?.stopPropagation();
    if (!requestId) return;
    setRevokingId(requestId);
    try {
      await services.revokePartConfirmation(requestId);
      loadRequests();
    } catch {
      setError("Failed to revoke this request. Please try again.");
    } finally {
      setRevokingId(null);
    }
  };

  if (loading) {
    return (
      <Flex
        w="100%"
        h="100%"
        bg="white"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="lg"
        overflow="hidden"
        boxShadow="sm"
      >
        <Box
          w="280px"
          flexShrink={0}
          borderRight="1px solid"
          borderColor="gray.200"
          p={3}
        >
          <VStack spacing={3} align="stretch">
            {[1, 2, 3, 4].map((i) => (
              <Box
                key={i}
                p={3}
                borderRadius="md"
                border="1px solid"
                borderColor="gray.100"
              >
                <Skeleton height="16px" width="140px" mb={2} />
                <Skeleton height="12px" width="100px" />
              </Box>
            ))}
          </VStack>
        </Box>
        <Box flex={1} p={5} bg="gray.50">
          <Skeleton height="20px" width="180px" mb={3} />
          <Skeleton height="14px" width="280px" />
        </Box>
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex
        align="center"
        justify="center"
        w="100%"
        h="100%"
        bg="white"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="lg"
        boxShadow="sm"
      >
        <Text
          color="red.500"
          fontWeight="600"
          fontFamily="'Nunito','Inter', sans-serif"
        >
          {error}
        </Text>
      </Flex>
    );
  }

  if (requests.length === 0) {
    return (
      <Flex
        align="center"
        justify="center"
        w="100%"
        h="100%"
        bg="white"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="lg"
        boxShadow="sm"
        direction="column"
        gap={4}
        py={10}
      >
        <Icon as={FaRegFileAlt} boxSize={12} color="gray.300" />
        <Text
          color="gray.500"
          fontWeight="700"
          fontSize="lg"
          fontFamily="'Nunito','Inter', sans-serif"
        >
          No Part Confirmations Found
        </Text>
        <Text
          color="gray.400"
          fontSize="sm"
          textAlign="center"
          maxW="320px"
          fontFamily="'Nunito','Inter', sans-serif"
        >
          There are no part confirmation requests for this enquiry yet.
        </Text>
      </Flex>
    );
  }

  return (
    <Flex
      w="100%"
      h="100%"
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="lg"
      overflow="hidden"
      boxShadow="sm"
    >
      <Box
        w="280px"
        flexShrink={0}
        borderRight="1px solid"
        borderColor="gray.200"
        overflowY="auto"
        p={3}
        bg="white"
      >
        <VStack spacing={2.5} align="stretch">
          {requests.map((request, idx) => {
            const isSelected = idx === selectedIndex;
            return (
              <Box
                key={request.requestId || idx}
                onClick={() => setSelectedIndex(idx)}
                p={3}
                borderRadius="md"
                cursor="pointer"
                transition="all 0.2s"
                bg={isSelected ? "purple.50" : "white"}
                border="1px solid"
                borderColor={isSelected ? "purple.200" : "gray.100"}
                borderLeft={isSelected ? "4px solid" : "4px solid transparent"}
                borderLeftColor={isSelected ? "purple.500" : "transparent"}
                boxShadow="sm"
                _hover={
                  isSelected ? {} : { bg: "gray.50", borderColor: "gray.200" }
                }
              >
                <Flex justify="space-between" align="center" mb={1} gap={2}>
                  <Tooltip label={request.supplierName} hasArrow>
                    <Text
                      fontSize="0.85rem"
                      fontWeight="800"
                      color={isSelected ? "purple.700" : "gray.700"}
                      fontFamily="'Nunito','Inter', sans-serif"
                      noOfLines={1}
                    >
                      {request.supplierName}
                    </Text>
                  </Tooltip>
                  <Badge
                    colorScheme={statusColor(request.status)}
                    borderRadius="md"
                    px={1.5}
                    fontSize="0.65rem"
                    fontFamily="'Nunito','Inter', sans-serif"
                    flexShrink={0}
                  >
                    {request.status}
                  </Badge>
                </Flex>
                <Flex justify="space-between" align="center" gap={2}>
                  <Text
                    fontSize="0.75rem"
                    color="gray.500"
                    fontWeight="500"
                    fontFamily="'Nunito','Inter', sans-serif"
                  >
                    {request.createdAt
                      ? dayjs(request.createdAt).format("DD MMM YYYY, hh:mm A")
                      : "-"}
                  </Text>
                  {request.status === "PENDING" && (
                    <Button
                      size="xs"
                      colorScheme="red"
                      variant="outline"
                      onClick={(event) =>
                        handleRevoke(request.requestId, event)
                      }
                      isLoading={revokingId === request.requestId}
                      fontFamily="'Nunito','Inter', sans-serif"
                      flexShrink={0}
                    >
                      Revoke
                    </Button>
                  )}
                </Flex>
              </Box>
            );
          })}
        </VStack>
      </Box>

      <Box flex={1} overflowY="auto" p={3} pt={5} bg="gray.50">
        <VStack spacing={3} align="stretch">
          <Box borderBottom="1px solid" borderBottomColor="gray.200">
            <Box>
              <Text {...estimateSectionHeaderProps}>Parts</Text>
            </Box>
          </Box>

          {(selected.parts || []).map((part) => (
            <Box
              key={part.partId || part.partNumber}
              bg="white"
              borderRadius="lg"
              border="1px solid"
              borderColor="gray.200"
              p={4}
            >
              <Flex justify="space-between" align="flex-start" gap={4}>
                <Box>
                  <Text
                    fontWeight="800"
                    fontFamily="'Nunito','Inter', sans-serif"
                    lineHeight="1.2"
                  >
                    {part.partNumber || "-"}
                  </Text>
                  <Text
                    fontSize="0.7rem"
                    color="gray.600"
                    lineHeight="1.2"
                    mt="2px"
                  >
                    {part.partName || "-"}
                  </Text>
                  <Text
                    fontWeight="700"
                    fontFamily="'Nunito','Inter', sans-serif"
                    lineHeight="1.2"
                    color="gray.500"
                    mt="4px"
                    fontSize="0.9rem"
                  >
                    Asked: {part.askedQty}
                    {part.confirmedQty != null
                      ? ` · Confirmed: ${part.confirmedQty}`
                      : ""}
                  </Text>
                </Box>
                <Badge
                  colorScheme={
                    part.isRevoked
                      ? "red"
                      : part.responseStatus === "confirmed"
                        ? "green"
                        : part.responseStatus === "declined"
                          ? "orange"
                          : "gray"
                  }
                  flexShrink={0}
                >
                  {part.isRevoked ? "revoked" : part.responseStatus}
                </Badge>
              </Flex>
            </Box>
          ))}
        </VStack>
      </Box>
    </Flex>
  );
};

export default AllPartConfirmationsView;
